/**
 * "WHAT THE ADVICE WAS BASED ON", COMPLETE (owner, 1 Oct 2026: "a section for every screen with every
 * response captured").
 *
 * The oversight view first borrowed the Statement of Advice's appendix walker, which is selective by
 * design — the document shows assets, debts and income in its own tables, so its appendix skips them.
 * A compliance reviewer needs the opposite: everything the client told us, laid out the way they
 * entered it. So this walks the sealed fact-find screen by screen, in the order of the fact-find's own
 * steps, and lists every answer — each asset and debt line by line. Anything that belongs to no known
 * screen still appears, under "Other answers recorded", so nothing can silently drop out.
 *
 * Only values the app injects itself (firm assumptions, saved app state) are left out; they are not
 * the client's answers, and the technical record below still holds them.
 */

export interface BasisRow {
  label: string;
  value: string;
}

export interface BasisSection {
  title: string;
  rows: BasisRow[];
}

type Json = unknown;

/** Keys the app writes for itself, not answers anyone gave. */
const NOT_ANSWERS = new Set(['firmAssumptions', '__appState', '__appData', '__grannyFlatFundingMeta']);
/** Internal identifiers inside an answer: meaningless to a reader. */
const SKIP_FIELDS = new Set(['id', 'entityId', 'assetLink']);

const LABELS: Record<string, string> = {
  adviceFor: 'Advice is for',
  completer: 'Completed by',
  completerFirstName: 'Completed by — first name',
  completerLastName: 'Completed by — last name',
  completerRelationship: 'Completed by — relationship',
  dob: 'Date of birth',
  phi: 'Private health insurance',
  preferredPhone: 'Phone',
  currentResidency: 'Living arrangement',
  healthStatus: 'Health',
  transferBalanceUsed: 'Transfer balance used',
  acat: 'Aged care assessment',
  clientAcat: 'Client — aged care assessment',
  partnerAcat: 'Partner — aged care assessment',
  rad: 'RAD',
  dap: 'DAP',
  phiNotes: 'Private health insurance notes',
  livingExpenses: 'Living expenses (a year)',
};

const prettify = (key: string): string => {
  if (LABELS[key]) return LABELS[key]!;
  const words = key
    .replace(/^__/, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .trim()
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/** Stored codes in plain words: "own_home" -> "Own home", "sah_level_7" -> "Support at Home level 7". */
const CODE_WORDS: Record<string, string> = {
  own_home: 'Own home',
  with_family: 'Living with family',
  aged_care: 'Residential aged care',
  home_care: 'Care at home',
  support_at_home: 'Support at Home',
  in_progress: 'In progress',
  pi: 'Principal and interest',
  io: 'Interest only',
};
function humanise(text: string): string {
  if (!/^[a-z0-9_]+$/.test(text)) return text; // free text and anything already readable
  if (CODE_WORDS[text]) return CODE_WORDS[text]!;
  const sah = /^sah_level_(\d+)$/.exec(text);
  if (sah) return `Support at Home level ${sah[1]}`;
  const hcp = /^hcp_transition_(\d+)$/.exec(text);
  if (hcp) return `Home Care Package level ${hcp[1]} (transitioned)`;
  const words = text.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

const show = (v: Json): string | null => {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'number') return Number.isFinite(v) ? v.toLocaleString('en-AU') : null;
  if (typeof v === 'string') return humanise(v.trim()) || null;
  return null;
};

/** Every answer under `value`, labelled by its path ("Bank accounts 1 (Westpac) › Value"). */
function flatten(value: Json, label: string, out: BasisRow[]): void {
  if (Array.isArray(value)) {
    value.forEach((item, i) => {
      if (item && typeof item === 'object' && !Array.isArray(item)) {
        const desc = show((item as Record<string, Json>).description ?? (item as Record<string, Json>).name);
        flatten(item, `${label} ${i + 1}${desc ? ` (${desc})` : ''}`, out);
      } else {
        const v = show(item);
        if (v !== null) out.push({ label: `${label} ${i + 1}`, value: v });
      }
    });
    return;
  }
  if (value && typeof value === 'object') {
    for (const [k, v] of Object.entries(value as Record<string, Json>)) {
      if (SKIP_FIELDS.has(k)) continue;
      flatten(v, label ? `${label} › ${prettify(k)}` : prettify(k), out);
    }
    return;
  }
  const v = show(value);
  if (v !== null) out.push({ label: label || 'Answer', value: v });
}

/** The fact-find's screens, in order, and the parts of the file each one collects. */
const SCREENS: readonly { title: string; keys: readonly string[] }[] = [
  { title: "Who's completing", keys: ['completer', 'completerFirstName', 'completerLastName', 'completerRelationship', 'adviceFor'] },
  { title: 'Your details', keys: ['client', 'partner', 'relationship'] },
  { title: 'Goals', keys: ['goals'] },
  { title: 'Living arrangements', keys: ['home', 'rent', 'retirementVillage', 'grannyFlat', 'livingWithFamily'] },
  { title: 'Care needs', keys: ['careNeeds', 'servicePlan', 'sahContributionRateOverride'] },
  { title: 'Assets', keys: ['assets'] },
  { title: 'Trusts and companies', keys: ['structures', 'structuresGateAnswer', 'structureFundingInSuggestions'] },
  { title: 'Liabilities', keys: ['liabilities', 'liabilitiesConfirmedNone'] },
  { title: 'Income and expenses', keys: ['income', 'expenses', 'giftsHistory'] },
  { title: 'Family and estate', keys: ['family'] },
];

/** What each bag is called when its answers are listed ('' = its own entries name themselves). */
const BAG_PREFIX: Record<string, string> = {
  client: 'Client',
  partner: 'Partner',
  relationship: 'Relationship',
  home: 'Home',
  rent: 'Renting',
  retirementVillage: 'Retirement village',
  grannyFlat: 'Granny flat',
  livingWithFamily: 'Living with family',
  servicePlan: 'Service plan',
  sahContributionRateOverride: 'Contribution rates entered',
  income: 'Income',
  expenses: 'Expenses',
  giftsHistory: 'Gifts',
};

/*
 * ASSETS SHOW ONLY THE QUESTIONS THEIR SCREEN ASKS (owner, 1 Oct 2026). Every new asset is created
 * from one template (buildNewAsset.tsx) carrying defaults for every kind — franking 70%, funding source
 * "super", an annuity start-date option — and the asset screen only shows the questions that apply to
 * the type, so those defaults sit in the file unseen. A bank account listing "Funding source: Super"
 * reads as an answer the client gave; it is not. These lists mirror the questions on the Assets step
 * (ClientProfileSteps.tsx, the per-type switch); the raw record still holds everything.
 */
const ASSET_COMMON = ['description', 'value', 'ownership'] as const;
const ASSET_QUESTIONS: Record<string, readonly string[]> = {
  bankAccounts: ['incomeRate'],
  termDeposits: ['incomeRate', 'maturityDate'],
  shares: ['incomeRate', 'frankingCredit', 'growthRate', 'costBase', 'acquiredOn', 'reinvest'],
  investmentBonds: ['growthRate', 'commencementDate'],
  investmentProperty: ['growthRate', 'costBase', 'acquiredOn', 'rent', 'expenses'],
  superannuation: ['growthRate'],
  accountBasedPensions: ['growthRate', 'income', 'commencementDateOption', 'commencementDate', 'commencementDateUnsure', 'purchasePrice'],
  annuities: ['income', 'annuityType', 'commencementDate', 'term', 'purchasePrice', 'rcv', 'fundingSource'],
  funeralBonds: [],
  prepaidFuneral: [],
  loansToOthers: ['incomeRate', 'purchasePrice', 'commencementDate'],
  personalAssets: ['growthRate'],
  other: ['income', 'expenses', 'growthRate', 'costBase', 'acquiredOn'],
};
const ASSET_KIND: Record<string, string> = {
  bankAccounts: 'Bank account',
  termDeposits: 'Term deposit',
  shares: 'Shares and managed funds',
  investmentBonds: 'Investment bond',
  investmentProperty: 'Investment property',
  superannuation: 'Super',
  accountBasedPensions: 'Account-based pension',
  annuities: 'Annuity',
  funeralBonds: 'Funeral bond',
  prepaidFuneral: 'Prepaid funeral',
  loansToOthers: 'Money lent to others',
  personalAssets: 'Personal assets',
  other: 'Other asset',
};
/** A loan's fields are named for what they hold on that card (ClientProfileSteps.tsx). */
const LOAN_FIELD: Record<string, string> = { value: 'Still owed', ownership: 'Lent by', purchasePrice: 'Amount first lent', commencementDate: 'Date lent', incomeRate: 'Interest charged (% a year)' };
const ASSET_FIELD: Record<string, string> = {
  description: 'Description',
  value: 'Current value',
  ownership: 'Owner',
  incomeRate: 'Income rate (% a year)',
  growthRate: 'Growth rate (% a year)',
  frankingCredit: 'Franking credit (%)',
  costBase: 'Cost base',
  acquiredOn: 'Month acquired',
  reinvest: 'Reinvest distributions',
  maturityDate: 'Maturity date',
  commencementDate: 'Start date',
  commencementDateOption: 'Started',
  commencementDateUnsure: 'Start date not known',
  purchasePrice: 'Purchase price',
  income: 'Annual payment or income',
  rent: 'Annual rent',
  expenses: 'Annual expenses',
  annuityType: 'Annuity type',
  term: 'Term (years)',
  rcv: 'Residual capital value',
  fundingSource: 'Funded from',
};

function assetRows(assets: Json): BasisRow[] {
  const rows: BasisRow[] = [];
  if (!assets || typeof assets !== 'object') return rows;
  for (const [category, list] of Object.entries(assets as Record<string, Json>)) {
    if (!Array.isArray(list)) continue;
    const fields = ASSET_QUESTIONS[category];
    list.forEach((raw, i) => {
      if (!raw || typeof raw !== 'object') return;
      const item = raw as Record<string, Json>;
      const desc = show(item.description);
      const head = `${ASSET_KIND[category] ?? prettify(category)} ${i + 1}${desc ? ` (${desc})` : ''}`;
      if (!fields) {
        // A kind of asset this view does not know yet: show all of it rather than hide an answer.
        flatten(item, head, rows);
        return;
      }
      for (const field of [...ASSET_COMMON, ...fields]) {
        if (field === 'description') continue; // already in the heading
        const v = show(item[field]);
        const label = (category === 'loansToOthers' && LOAN_FIELD[field]) || ASSET_FIELD[field] || prettify(field);
        if (v !== null) rows.push({ label: `${head} › ${label}`, value: v });
      }
    });
  }
  return rows;
}

export function buildAdviceBasisSections(factFind: Json): BasisSection[] {
  const fd = (factFind && typeof factFind === 'object' ? factFind : {}) as Record<string, Json>;
  const sections: BasisSection[] = [];
  const used = new Set<string>();

  for (const screen of SCREENS) {
    const rows: BasisRow[] = [];
    for (const key of screen.keys) {
      used.add(key);
      if (!(key in fd)) continue;
      const v = fd[key];
      // A screen's own bag is labelled by what is inside it, under a plain prefix where the screen
      // holds more than one ("Home › Value", "Expenses › Living expenses"); a list or a single answer
      // is labelled by its own name.
      if (key === 'assets') rows.push(...assetRows(v));
      else if (v && typeof v === 'object' && !Array.isArray(v)) flatten(v, BAG_PREFIX[key] ?? '', rows);
      else flatten(v, prettify(key), rows);
    }
    if (rows.length > 0) sections.push({ title: screen.title, rows });
  }

  const other: BasisRow[] = [];
  for (const [key, v] of Object.entries(fd)) {
    if (used.has(key) || NOT_ANSWERS.has(key)) continue;
    flatten(v, prettify(key), other);
  }
  if (other.length > 0) sections.push({ title: 'Other answers recorded', rows: other });
  return sections;
}
